import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import api from "../api/Api.js";
import { ArrowLeft, Plus, Trash2, Save, CheckCircle } from "lucide-react";
import "bootstrap/dist/css/bootstrap.min.css";

const ConfirmationhecklistAddPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // para 1 = Vehicle Checklist, para 2 = Salary Checklist
  const para = location.pathname.includes("salary-ddt-checklist") ? 2 : 1;

  const [rows, setRows] = useState([{ item_name: "", short_name: "" }]);
  const [loading, setLoading] = useState(false);

  const handleChange = (index, field, value) => {
    const updated = [...rows];
    updated[index][field] = value;
    setRows(updated);
  };

  const addMore = () => {
    if (rows.length >= 10) {
      toast.error("Maximum 10 items allowed at once");
      return;
    }
    setRows([...rows, { item_name: "", short_name: "" }]);
  };

  const removeRow = (index) => {
    if (rows.length === 1) {
      toast.error("At least one row is required");
      return;
    }
    setRows(rows.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    // Validation
    for (let row of rows) {
      if (!row.item_name.trim()) {
        toast.error("Item name is required for all rows");
        return;
      }
      if (para === 2 && !row.short_name.trim()) {
        toast.error("Short code is required for all rows");
        return;
      }
    }

    setLoading(true);
    try {
      const res = await api.post("/adminmod/items-add", { para, items: rows });

      if (res.data.success) {
        toast.success("Items added successfully");
        navigate(
          para === 1
            ? "/master/confirmation-checklist"
            : "/master/salary-ddt-checklist"
        );
      } else {
        toast.error(res.data.message);
      }
    } catch {
      toast.error("Server error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const pageTitle = para === 1 ? "Add Vehicle Checklist Items" : "Add Salary Deduction Components";

  return (
    <div className="container-fluid py-3 bg-white min-vh-100">
      
      {/* HEADER SECTION */}
      <div className="card shadow-sm border-0 mb-4 bg-primary-subtle rounded-3">
        <div className="card-body d-flex flex-wrap align-items-center justify-content-between p-3">
          <div>
            <h4 className="mb-1 fw-bold text-dark">{pageTitle}</h4>
            <p className="text-muted small mb-0">Add multiple items to your master list</p>
          </div>
          
          <Link
            to={para === 1 ? "/master/confirmation-checklist" : "/master/salary-ddt-checklist"}
            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2"
          >
            <ArrowLeft size={16} /> Back to List
          </Link>
        </div>
      </div>

      {/* MAIN FORM CONTENT - FULL WIDTH */}
      <div className="card shadow-sm border-0 rounded-3">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="bg-light">
                <tr>
                  <th className="text-center py-3 text-secondary text-uppercase small fw-bold" style={{ width: "5%" }}>#</th>
                  <th className="py-3 text-secondary text-uppercase small fw-bold" style={{ width: para === 2 ? "50%" : "80%" }}>
                    Item Name
                  </th>
                  {para === 2 && (
                    <th className="py-3 text-secondary text-uppercase small fw-bold" style={{ width: "30%" }}>
                      Short Code
                    </th>
                  )}
                  <th className="py-3 text-center text-secondary text-uppercase small fw-bold" style={{ width: "10%" }}>
                    Remove
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={index}>
                    <td className="text-center text-muted fw-bold">{index + 1}</td>
                    
                    {/* Item Name Input */}
                    <td>
                      <input
                        type="text"
                        className="form-control"
                        placeholder={para === 1 ? "e.g., Check Engine Oil" : "e.g., Provident Fund"}
                        value={row.item_name}
                        onChange={(e) => handleChange(index, "item_name", e.target.value)}
                        autoFocus={index === rows.length - 1} // Auto-focus on new row
                      />
                    </td>

                    {/* Short Code Input (Salary Only) */}
                    {para === 2 && (
                      <td>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Code"
                          value={row.short_name}
                          onChange={(e) => handleChange(index, "short_name", e.target.value)}
                        />
                      </td>
                    )}

                    {/* Remove Action */}
                    <td className="text-center">
                      <button
                        className="btn btn-light text-danger btn-sm border-0 hover-shadow"
                        onClick={() => removeRow(index)}
                        disabled={rows.length === 1}
                        title="Remove Row"
                        style={{ width: "32px", height: "32px", borderRadius: "50%" }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FOOTER ACTIONS */}
        <div className="card-footer bg-white p-3 d-flex justify-content-between align-items-center">
          <button
            className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2 border-dashed px-3"
            onClick={addMore}
          >
            <Plus size={16} /> Add Another Row
          </button>

          <button
            className="btn btn-success d-flex align-items-center gap-2 px-4 fw-medium"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <div className="spinner-border spinner-border-sm text-white" role="status"></div>
            ) : (
              <CheckCircle size={18} />
            )}
            Save Items
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationhecklistAddPage;